<template>
  <div id="home" style="background-image: url(https://cattlytics.blob.core.windows.net/assets/background-image-1.jpg);">
    <v-container fluid justify-center>
      
      <!-- Loading -->
      <template v-if="isLoading">
        <loading
          :active.sync="isLoading"
          :can-cancel="false"
          loader="dots"
          :color="appColor_Progress"
        />
      </template>

      <v-row justify="center">

        <!-- If: Unauthenticated -->
        <template v-if="!authenticated">
          <v-col cols="12" sm="12" :class="bannerClass">
            <span>Welcome to Cattlytics</span>
          </v-col>
        </template>

        <!-- Else: Authenticated -->
        <template v-else>
          <v-container fluid>

            <template v-if="!isLoading">
              <!-- Cattlytics Banner -->
              <v-row no-gutters>
                <v-col cols="12" sm="12" lg="12">
                  <span :class="bannerClass">Cattlytics</span>
                </v-col>
              </v-row>

              <!-- Welcome Message -->
              <v-row no-gutters>
                <v-col cols="12" sm="12" lg="12">
                  <span :class="welcomeClass">
                    {{ welcomeText }}
                  </span>
                </v-col>
              </v-row>

              <!-- If: Not Sale Day Mode -->
              <template v-if="!useSaleDayMode || !isSaleDayUser">
                <v-row no-gutters>
                  <v-col cols="12" sm="12" lg="12">
                    <span :class="companyNameClass">
                      {{ companyName }}
                    </span>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12" sm="12" lg="12">
                    <v-btn 
                      dark
                      :color="appColor_ContentToolbar"
                      class=" mt-10"
                      @click="launchBullFinder"
                      >
                      <span>Launch Bull Finder</span>
                    </v-btn>
                  </v-col>
                </v-row>
              </template>

              <!-- Else: Sale Day Mode -->
              <template v-else >
                <v-row no-gutters>
                  <v-col cols="12" sm="12" lg="12">
                    <v-card class="mt-5">

                      <!-- List of Bull Sales -->
                      <v-toolbar flat :class="salesHeaderClass">
                        <v-toolbar-title :class="isSmallDevice ? 'title' : 'headline'">
                          Current Sales On Bull Finder
                        </v-toolbar-title>
                      </v-toolbar>
                      
                      <v-list>
                        <v-list-item-group v-for="(s, i) in sales" :key="s.id" >
                          <v-list-item @click="saleClicked(s)">
                            <v-list-item-action>
                              <v-img :src="s.animalSource.logoUrl" :max-width="logoMaxWidth"></v-img>
                            </v-list-item-action>
                            <v-list-item-content>
                              <v-list-item-title :class="saleNameClass">
                                {{ s.name }}
                              </v-list-item-title>
                              <v-list-item-subtitle :class="saleDateClass">
                                {{ s.saleDateDesc }}
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-divider v-if="i < sales.length - 1"/>
                        </v-list-item-group>
                      </v-list>
                    </v-card>
                  </v-col>
                </v-row>
              </template>
            </template>
          </v-container>
        </template>      
      </v-row>
 
    </v-container>

    <!-- Error Dialog -->
    <v-dialog v-model="errorDialog" max-width="500px">
      <app-error
        v-bind:errorTitle="errorTitle"
        v-bind:errorText="errorText"
        v-on:close="errorDialog = false"
      />
    </v-dialog>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { AppColor, AnimalSourceId } from '@/config.js'
import appService from '@/app.service'
import AppError from '@/components/AppError'

export default {
  name: 'Home',
  components: {
    AppError
  },
  data () {
    return {
      appColor_Light: AppColor.Light,
      appColor_ContentToolbar: AppColor.ContentToolbar,
      appColor_Progress: AppColor.Progress,
      animalSourceId_Beefbooster: AnimalSourceId.Beefbooster,

      isAuthenticated: this.authenticated,
      isLoading: false,

      // error handling
      errorDialog: false,
      errorTitle: 'Internal Error',
      errorText: null     
    }
  },
  computed: {
    ...mapGetters([
      'authenticated',
      'profile',
      'appSettings',
      'bullSales',
      'currentBullSale',
      'saleDateAccounts'
    ]),
    isAdmin () {
      if (this.isNullOrUndefined(this.profile)) return false
      return !this.isNullOrUndefined(this.profile.roles)
        ? this.profile.roles.includes('Admin')
        : false
    },
    isBreeder () {
      if (this.isNullOrUndefined(this.profile)) return false
      return !this.isNullOrUndefined(this.profile.roles)
        ? this.profile.roles.includes('Breeder')
        : false
    },
    isAdminy () {
      return this.isAdmin || this.isBreeder
    },
    sales () {
      return !this.isNullOrUndefined(this.bullSales)
        ? this.bullSales
        : []
    },
    companyName () {
      if (this.isNullOrUndefined(this.profile.name)) return null

      // check if they entered a company into their profile
      var isUserCompany = !this.isNullOrUndefined(this.profile.companyName)
      if (isUserCompany) isUserCompany = this.profile.companyName.length > 0
      const defaultCompany = isUserCompany
        ? this.profile.companyName
        : null

      // beefbooster customers
      if (this.isBeefboosterValidated) {
        return !isUserCompany && this.beefboosterCompanyName !== null
           ? this.beefboosterCompanyName
           : defaultCompany
      } else { // non-beefbooster customers
        return defaultCompany
      }
    },
    isBeefboosterValidated () {
      return !this.isNullOrUndefined(this.profile.bbCustomerStatus)
        ? this.profile.isBeefboosterCustomer && this.profile.bbCustomerStatus.isValid
        : false
    },
    beefboosterCompanyName () {
      if (this.isNullOrUndefined(this.profile.bbCustomerStatus))
        return null
    
      if (this.isNullOrUndefined(this.profile.bbCustomerStatus.companyName))
        return null

      return this.profile.bbCustomerStatus.companyName.length > 0
        ? this.profile.bbCustomerStatus.companyName
        : null
    },
    isHerdtraxValidated () {
      return !this.isNullOrUndefined(this.profile.hxCustomerStatus)
        ? this.profile.isHerdtraxCustomer && this.profile.hxCustomerStatus.isValid
        : false
    },
    isSmallDevice () {
      return this.$vuetify.breakpoint.name === 'xs' || this.$vuetify.breakpoint.name === 'sm'
    },
    bannerClass () {
      const fontMSmall = 'headline'
      const fontLarge = 'display-2'
      const common = 'font-weight-black blue-grey--text text--lighten-4'

      return this.isSmallDevice
        ? `${fontMSmall} ${common} mb-0`
        : `${fontLarge} ${common} mb-10`
    },

    // non sale day mode classes
    welcomeText () {
      if (this.isNullOrUndefined(this.profile.name))
        return null
      
      const name = this.profile.name.indexOf('@') < 0
        ? this.profile.name.split(' ')[0]
        : this.profile.nickname

      return name !== null
        ? `Welcome, ${name}!`
        : null
    },
    welcomeClass () {
      const fontSmall = 'subtitle-1'
      const fontLarge = 'display-1'
      const common = `font-weight font-italic blue-grey--text text--lighten-4 mb-4`

      return this.isSmallDevice
        ? `${fontSmall} ${common}`
        : `${fontLarge} ${common}`
    },
    companyNameClass () {
      const common = 'font-weight blue--text text--lighten-2'
      const fontSmall = 'title'
      const fontLarge = 'display-2'

      return this.isSmallDevice
        ? `${fontSmall} ${common}`
        : `${fontLarge} ${common}`
    },

    // sale day mode classes
    salesHeaderClass () {
      const fontSmall = 'subtitle-1'
      const fontLarge = 'display-1'
      const common = `font-weight grey--text text--lighten-3 ${this.appColor_Light}`

      return this.isSmallDevice
        ? `${fontSmall} ${common}`
        : `${fontLarge} ${common}`
    },
    saleName () {
      return !this.isNullOrUndefined(this.currentBullSale)
        ? this.currentBullSale.name
        : null
    },
    saleNameClass () {
      const common = 'font-weight-black grey--text text--darken-2 text-wrap'
      return this.isSmallDevice
        ? `title ${common}`
        : `headline ${common}`
    },
    saleDateClass () {
      const common = 'grey--text text-wrap'
      return this.isSmallDevice
        ? `subtitle-1 ${common}`
        : `title ${common}`
    },
    logoMaxWidth () {
      return this.isSmallDevice ? "90" : "140"
    },
    isSaleDayUser () {
      const self = this
      if (!this.isBeefboosterValidated) return false
      if (self.isNullOrUndefined(self.saleDateAccounts)) return false
      if (self.sales.length === 0) return false

      const accountNo = self.profile.bbCustomerStatus.accountNo
      const isBreeder = self.isBreeder
      const sourceId = self.animalSourceId_Beefbooster
      const saleDate = self.sales.filter(s => { 
          return s.isBreederDay === isBreeder && s.animalSource.id === sourceId
        })[0].saleDate

      var accountExists = false
      self.saleDateAccounts.forEach(sda => {
        const accounts = sda.accounts.filter(a => {
          return a.accountNo === accountNo
            && self.getDate(sda.saleDate) === self.getDate(saleDate)
          })
        if (!accountExists) accountExists = accounts.length > 0
      })
      return accountExists
    },
    useSaleDayMode () {
      if (!this.appSettings.isSaleDayMode) return false
      if (this.sales.length === 0) return false

      return this.isSaleDayUser || this.isAdminy
    }
  },
  methods: {
    ...mapActions(['login', 'logout']),

    loadData () {
      const self = this
      self.isLoading = true
      appService.getAnimalSources().then(data => {
        if (data.errorSeverity !== null) {
          this.errorSeverity = data.errorSeverity
          this.errorMessage = data.errorMessage
          this.errorDialog = true
        } else {
          const animalSources = data.sources
          self.$store.dispatch('storeAnimalSources', { animalSources })

          if (this.appSettings.isSaleDayMode) {
            this.loadSales()
          } else {
            self.isLoading = false
          }
        }
      }).catch(() => { self.isLoading = false })
    },

    loadSales () {
      const self = this
      appService.getBullFinderSales().then(data => {
        if (data.errorSeverity === null) {
          const isBreeder = self.isBreeder
          const sales = data.sales.filter(s => { return s.isBreederDay === false || isBreeder })
          self.$store.dispatch('storeBullSales', { sales })

          const springSaleSn = this.sales[0].springSaleSn
          appService.getSaleDateAccounts(springSaleSn).then(data => {
            const saleDateAccounts = data.saleDateAccounts
            self.$store.dispatch('storeSaleDateAccounts', { saleDateAccounts })
            self.isLoading = false
          }).catch(() => { self.isLoading = false })
        }
      }).catch(() => { self.isLoading = false})
    },

    saleClicked (item) {
      const sale = item
      this.$store.dispatch('storeCurrentBullSale', { sale })
      this.$router.push({ path: '/bullFinder', query: {
        saleId: sale.id
      }}).catch(() => {});
    },

    launchBullFinder () {
      this.$router.push('/bullFinder')
    },

    getDate (date) {
      if (date !== null) {
        if (date.length > 0) {
          return this.$moment(date).format('YYYY-MM-DD')
        }
      }
      return null
    },

    isNullOrUndefined (obj) {
      return obj === undefined 
        || typeof obj === 'undefined' 
        || obj === null
    }
  },
  watch: {
    authenticated (value) {
      if (value === null) return
      this.isAuthenticated = value
      if (value) this.loadData()
    }
  }
}
</script>

<style scoped>
#home {
  background-size: cover;
  background-attachment: fixed;
  height: 90vh;
  text-align: center;
}
</style>
